import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  Output,
  ViewChild,
  inject,
} from '@angular/core';
import { PartnerDealCommitteeViewResponse } from '@interfaces/partners/responses/partner-deal-committee.response';
import { TranslateService } from '@ngx-translate/core';
import { ToastHelperService } from '@services/helpers/toast-helper.service';
import { PartnerDealCommitteeHttpService } from '@services/http/partners/partner-deal-committee-http.service';
import { VaultBaseComponent } from '@src/app/shared/components/base/vault-base.component';
import { DialogComponent } from '@src/app/shared/components/dialog/dialog.component';
import { SharedModule } from '@src/app/shared/shared.module';
import { first } from 'rxjs';
import { joinNames } from '@app/features/dashboard/commercial/pages/deal-detail/components/deal-committee-panel/deal-committee-presenter';

/** Tamanho máximo do resumo, o mesmo do backend (`summary` <= 4000). */
const SUMMARY_MAX_LENGTH = 4000;

/**
 * Envio do deal ao comitê de crédito (card 3789), com o resumo opcional para os sócios.
 *
 * Abre de dois lugares: o cartão "próximo passo" da etapa Análise e o painel do comitê sem rodada em
 * votação (deal movido à mão ou reaberto). Ao abrir, lê o comitê do deal para dizer **quem vai
 * votar** e em quanto tempo, e se o servidor já sabe que o envio vai ser recusado (sem 3 votantes,
 * etapa errada), mostra o motivo e trava o botão em vez de deixar a pessoa descobrir no toast.
 */
@Component({
  selector: 'app-modal-committee-submit',
  standalone: true,
  templateUrl: './modal-committee-submit.component.html',
  styleUrls: ['./modal-committee-submit.component.scss'],
  imports: [CommonModule, FormsModule, SharedModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ModalCommitteeSubmitComponent extends VaultBaseComponent {
  @ViewChild('dialog') dialogRef: DialogComponent | null = null;

  /**
   * Chave do `app-dialog`. A modal existe duas vezes na tela do deal (cartão próximo passo e painel
   * do comitê): com a mesma chave, o `p-confirmDialog` das duas abriria junto.
   */
  @Input() dialogKey = 'committee-submit';

  /** O deal foi enviado: devolve o comitê como o servidor gravou. */
  @Output() submitted = new EventEmitter<PartnerDealCommitteeViewResponse | null>();

  private readonly committeeHttp = inject(PartnerDealCommitteeHttpService);
  private readonly toast = inject(ToastHelperService);
  private readonly cdr = inject(ChangeDetectorRef);

  readonly summaryMaxLength = SUMMARY_MAX_LENGTH;

  dealId = '';
  companyName = '';
  summary = '';
  loading = false;
  sending = false;
  view: PartnerDealCommitteeViewResponse | null = null;
  /** A leitura do comitê falhou: a modal ainda deixa enviar, e o servidor confere. */
  loadFailed = false;

  constructor(translateService: TranslateService) {
    super(translateService);
  }

  /** "Vão votar: Guilherme Araldi, Felipe Guimarães e Rafael Buzin." */
  get votersText(): string {
    const names = this.view?.configuredVoterNames ?? [];
    return names.length ? `Vão votar: ${joinNames(names)} (lista da Parametrização).` : '';
  }

  /** "Ao enviar, os sócios votantes recebem o pedido de voto... com prazo de 48 horas." */
  get deadlineText(): string {
    const hours = this.view?.deadlineHours;
    return hours
      ? `Ao enviar, os sócios votantes recebem o pedido de voto no sino e por e-mail, com prazo de ${hours} horas.`
      : 'Ao enviar, os sócios votantes recebem o pedido de voto no sino e por e-mail.';
  }

  /** Motivo do servidor para não enviar agora; nulo quando pode. */
  get blockedReason(): string | null {
    if (!this.view || this.view.canSubmit) return null;
    return (
      this.view.submitBlockedReason ||
      'Você não pode enviar este deal ao comitê. Quem envia é o comercial do deal ou quem estiver na lista de quem envia, em Parametrização > Comitê de crédito do deal.'
    );
  }

  /** Abre a modal limpa e lê o comitê do deal. */
  open(dealId: string, companyName: string): void {
    this.dealId = dealId;
    this.companyName = companyName;
    this.summary = '';
    this.sending = false;
    this.view = null;
    this.loadFailed = false;
    this.loading = true;
    this.cdr.markForCheck();
    this.dialogRef?.open('', '', '', 'Cancelar', '', this.dialogKey, false, true);

    this.committeeHttp
      .getCommittee(dealId)
      .pipe(first())
      .subscribe({
        next: view => {
          this.view = view;
          this.loading = false;
          this.cdr.markForCheck();
        },
        error: () => {
          this.loadFailed = true;
          this.loading = false;
          this.cdr.markForCheck();
        },
      });
  }

  close(): void {
    this.dialogRef?.close();
  }

  isFormValid(): boolean {
    return !this.loading && !this.sending && !this.blockedReason;
  }

  submitAction(): void {
    if (!this.isFormValid()) return;

    this.sending = true;
    this.cdr.markForCheck();

    this.committeeHttp
      .submit(this.dealId, { summary: this.summary.trim() || null })
      .pipe(first())
      .subscribe({
        next: view => {
          this.sending = false;
          const names = view?.current?.voters.map(v => v.name) ?? [];
          this.toast.showToastSuccess(
            names.length
              ? `Enviado ao comitê. ${joinNames(names)} recebem o pedido de voto no sino e por e-mail.`
              : 'Enviado ao comitê. Os sócios votantes recebem o pedido de voto no sino e por e-mail.'
          );
          this.submitted.emit(view);
          this.close();
          this.cdr.markForCheck();
        },
        error: err => {
          this.sending = false;
          this.toast.showToastError(
            err?.errors?.[0]?.message ??
              'Não foi possível enviar ao comitê agora. Confira a conexão e clique em Enviar ao comitê de novo.'
          );
          this.cdr.markForCheck();
        },
      });
  }
}
